import React, { useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaCode,
  FaPencilAlt,
  FaServer,
  FaChartLine,
  FaRocket,
  FaUser,
  FaVideo
} from 'react-icons/fa';

const About = ({ currentTheme }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const expertise = [
    { skill: 'Frontend Development', percentage: 90, icon: <FaCode />, color: currentTheme.primary },
    { skill: 'Technical Writing', percentage: 85, icon: <FaPencilAlt />, color: currentTheme.secondary },
    { skill: 'Backend Development', percentage: 80, icon: <FaServer />, color: '#FF6B6B' },
    { skill: 'Research Writing', percentage: 85, icon: <FaChartLine />, color: '#FFD700' },
    { skill: 'Data Science', percentage: 80, icon: <FaRocket />, color: '#9B59B6' },
    { skill: 'CCTV Systems', percentage: 85, icon: <FaVideo />, color: '#E67E22' }
  ];

  return (
    <Container
      fluid
      className="py-5 position-relative overflow-hidden"
      id="about"
      ref={ref}
      style={{ backgroundColor: currentTheme.background }}
    >
      {/* Animated Background */}
      <div className="position-absolute w-100 h-100 top-0 start-0" style={{ zIndex: -1 }}>
        <motion.div
          animate={{
            background: [
              `linear-gradient(45deg, ${currentTheme.primary}10 0%, ${currentTheme.secondary}10 100%)`,
              `linear-gradient(45deg, ${currentTheme.secondary}10 0%, ${currentTheme.primary}10 100%)`
            ],
          }}
          transition={{ duration: 3, repeat: Infinity, repeatType: 'reverse' }}
          className="w-100 h-100"
          style={{ position: 'absolute', top: 0, left: 0 }}
        />
      </div>

      <Container>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <Row className="mb-5">
            <Col lg={12} className="text-center">
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1 }}
              >
                <h2 className="display-4 fw-bold mb-4" style={{ color: currentTheme.primary }}>About Me</h2>
                <div className="d-flex justify-content-center align-items-center mb-5">
                  <div style={{ height: '4px', width: '50px', backgroundColor: currentTheme.primary }}></div>
                  <FaUser size={24} className="mx-3" style={{ color: currentTheme.primary }} />
                  <div style={{ height: '4px', width: '50px', backgroundColor: currentTheme.primary }}></div>
                </div>
              </motion.div>
            </Col>
          </Row>

          <Row className="align-items-center mb-5 gy-4">
            <Col lg={6}>
              <motion.div
                className="card border-0 shadow-sm h-100"
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
                whileHover={{ y: -10 }}
                style={{ 
                  backgroundColor: currentTheme.background === '#121212' ? '#1E1E1E' : '#FFFFFF', 
                  borderRadius: '15px' 
                }}
              >
                <div className="card-body p-4">
                  <h3 className="h4 fw-bold mb-4" style={{ color: currentTheme.primary }}>
                    Get to <span style={{ color: currentTheme.secondary, borderBottom: `2px solid ${currentTheme.secondary}` }}>know</span> me!
                  </h3>
                  <p className="mb-4" style={{ color: currentTheme.text, lineHeight: '1.8' }}>
                    As a <span style={{ color: currentTheme.primary, fontWeight: 'bold' }}>versatile IT professional</span>, I specialize in:
                    <ul style={{ color: currentTheme.text, lineHeight: '1.8' }}>
                      <li><span style={{ color: currentTheme.secondary, fontWeight: 'bold' }}>Frontend & Backend Development</span></li>
                      <li><span style={{ color: currentTheme.secondary, fontWeight: 'bold' }}>Technical & Research Writing</span></li>
                      <li><span style={{ color: currentTheme.secondary, fontWeight: 'bold' }}>CCTV Systems</span></li>
                      <li><span style={{ color: currentTheme.secondary, fontWeight: 'bold' }}>IT Administration</span></li>
                    </ul>
                  </p>
                  <p className="mb-4" style={{ color: currentTheme.text, lineHeight: '1.8' }}>
                    I <span style={{ color: currentTheme.secondary, fontWeight: 'bold' }}>love the process</span> of transforming <span style={{ color: currentTheme.primary, fontStyle: 'italic' }}>raw ideas</span> into <span style={{ color: currentTheme.primary, fontWeight: 'bold' }}>impactful websites and products</span>. My goal is to take on work that <span style={{ color: currentTheme.secondary, fontWeight: 'bold' }}>challenges me as a developer</span> and creates results I can be proud of.
                  </p>
                  <p className="mb-4" style={{ color: currentTheme.text, lineHeight: '1.8' }}>
                    I'm <span style={{ color: currentTheme.secondary, fontWeight: 'bold' }}>open to new collaborations</span> where I can contribute and grow. Feel free to <span style={{ color: currentTheme.secondary, fontWeight: 'bold' }}>connect with me</span> - you'll find my links in the footer.
                  </p>
                  <p className="mb-4" style={{ color: currentTheme.text, lineHeight: '1.8' }}>
                    Apart from coding, I have a passion for <span style={{ color: currentTheme.primary, fontWeight: 'bold' }}>writing</span>. Oh, and I <span style={{ color: currentTheme.secondary, fontStyle: 'italic' }}>love</span> ☕ coffee!
                  </p>
                </div>
              </motion.div>
            </Col>

            <Col lg={6}>
              <motion.div
                className="card border-0 shadow-sm h-100"
                initial={{ x: 100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
                style={{ 
                  backgroundColor: currentTheme.background === '#121212' ? '#1E1E1E' : '#FFFFFF', 
                  borderRadius: '15px' 
                }}
              >
                <div className="card-body p-4">
                  <h3 className="h4 fw-bold mb-4" style={{ color: currentTheme.primary }}>Core Expertise</h3>
                  {expertise.map((item, index) => (
                    <motion.div
                      key={index}
                      className="mb-4"
                      initial={{ x: 50, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <div className="me-3" style={{ color: item.color }}>{item.icon}</div>
                          <span className="fw-bold" style={{ color: currentTheme.text }}>{item.skill}</span>
                        </div>
                        <span className="fw-bold" style={{ color: currentTheme.primary }}>{item.percentage}%</span>
                      </div>
                      <div className="progress" style={{ height: '8px', backgroundColor: `${currentTheme.primary}10` }}>
                        <motion.div
                          className="progress-bar"
                          style={{ backgroundColor: item.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${item.percentage}%` }}
                          transition={{ duration: 1, delay: index * 0.2 }}
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Container>
    </Container>
  );
};

export default About;